import { useLayoutEffect, useState, useCallback, useRef } from 'react';
import Script from 'next/script';
import { IFreeEntryProps } from './types/IFreeEntryProps';

// Track global loading state across all FreeEntry components
const globalLoadingState = {
  // Stores a single Promise for loading JS dependencies across all instances (JS modules are singletons in Next.js/React)
  // This ensures JS dependencies are only loaded once, even if multiple
  // FreeEntry components exist on the same page, preventing duplicate network requests
  // and avoiding race conditions between components trying to load the same resources
  jsLoadingPromise: null as Promise<any> | null,
};

// Free entry component may depend on the nikonInteractive model being available if it contains interactive content.
// This component checks if nikonInteractive is available and will wait for it to be available before loading any dependencies.
// If dependencies fail to load or nikonInteractive is not available, the component will still render but interactive content may not work.

const FreeEntry = ({ data: { isExpired, content, uid, script_file, load_dependencies } }: IFreeEntryProps) => {
  const [jsLoaded, setJsLoaded] = useState(!load_dependencies); // Only track JS loading state
  const componentMountedRef = useRef(true);
  const noLoad = isExpired || !Boolean(content?.length);

  // Check if nikonInteractive is available
  const waitForNikonInteractive = useCallback((maxAttempts = 50, interval = 100): Promise<boolean> => {
    return new Promise((resolve) => {
      let attempts = 0;

      // Recursive function to check for nikonInteractive availability
      const checker = () => {
        // Check if nikonInteractive is available
        if (typeof window !== 'undefined' && window.nikonInteractive) {
          // Resolve the promise if available
          resolve(true);
          return;
        }

        // Reject the promise if max attempts reached
        if (attempts >= maxAttempts) {
          console.error('nikonInteractive not available after maximum attempts');
          resolve(false);
          return;
        }

        attempts++;
        // Wait for the next interval and check again
        setTimeout(checker, interval);
      };

      checker();
    });
  }, []);

  // Cleanup on unmount
  useLayoutEffect(() => {
    // Set mounted ref to false on unmount
    return () => {
      componentMountedRef.current = false;
    };
  }, []);

  // Load dependencies
  useLayoutEffect(() => {
    // Skip if content shouldn't load or dependencies aren't needed
    if (noLoad || !load_dependencies) {
      return;
    }

    // Helper function to load all dependencies
    const loadAllDependencies = async () => {
      try {
        // First check if nikonInteractive is available for JS
        const isReady = await waitForNikonInteractive();
        if (!isReady) return;

        // Load JS dependencies - create or reuse existing promise
        if (load_dependencies && !jsLoaded) {
          if (globalLoadingState.jsLoadingPromise) {
            await globalLoadingState.jsLoadingPromise;
            if (componentMountedRef.current) {
              setJsLoaded(true);
            }
            return;
          }
          // Create a new loading promise and store it globally
          globalLoadingState.jsLoadingPromise = window.nikonInteractive
            .loadPageDependencies()
            .catch((err: Error) => console.error('Error loading JavaScript dependencies:', err));

          // Wait for the loading to complete
          await globalLoadingState.jsLoadingPromise;

          // Only update state if component is still mounted
          if (componentMountedRef.current) {
            setJsLoaded(true);
          }
        }
      } catch (error) {
        console.error('Error in dependency loading:', error);
        if (componentMountedRef.current && load_dependencies) {
          setJsLoaded(true); // Still mark as loaded to avoid blocking rendering
        }
      }
    };
    // Start loading dependencies
    loadAllDependencies();
  }, [load_dependencies, waitForNikonInteractive, noLoad, jsLoaded]);

  // If the content is expired or empty, return null
  if (noLoad) {
    return null;
  }

  return (
    <div data-testid="free-entry" className="content-block">
      {script_file && jsLoaded && (
        <Script
          src={script_file.url}
          strategy="lazyOnload"
          onError={(e) => console.error(`Failed to load script: ${script_file.url}`, e)}
        />
      )}
      {content.map((block, index) => (
        <div data-testid="content-text" key={`${uid}-${index}`} dangerouslySetInnerHTML={{ __html: block }} />
      ))}
    </div>
  );
};

export default FreeEntry;
